import DocumentPage from './DocumentPage';

export const timeOffDocument = {
  slug: 'time-off',
  route: '/time-off',
  title: 'Time Off',
  category: 'People & Operations',
  format: 'DOCX',
  description:
    'The Time Off module in EduX ERP manages employee leave requests, approvals, allocations, balances, reporting, and leave configuration from one workflow.',
  metrics: [
    { label: 'Primary focus', value: 'Leave management' },
    { label: 'Core records', value: 'Requests and allocations' },
    { label: 'Visibility', value: 'Calendar and analysis' },
  ],
  workflow: [
    'Employees review the dashboard, submit leave from My Time Off, and check allocations from My Allocations.',
    'Managers and HR monitor the overview calendar, process requests, and maintain allocations from the management area.',
    'Administrators analyze leave data and configure time off types, accrual plans, holidays, mandatory days, and activity types.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Time Off Module in EduX ERP is used to manage employee leaves efficiently. It allows employees to request time off, managers to approve or reject requests, and HR to track leave balances, allocations, and reports.',
        'This module helps in managing employee leave requests, tracking leave balances, defining leave types such as Sick Leave and Casual Leave, and generating reports for analysis.',
      ],
      detailCards: [
        {
          title: 'What the module supports',
          bullets: [
            'Managing employee leave requests.',
            'Tracking leave balances.',
            'Defining leave types such as Sick Leave, Casual Leave, and similar policies.',
            'Generating reports for analysis.',
          ],
        },
      ],
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      body: [
        'The dashboard provides an overview of leave balances, scheduled leaves, public holidays, and mandatory days, helping users plan and manage time off effectively.',
        'It also allows users to view data in Day, Week, Month, and Year formats for better visualization.',
      ],
      figure: {
        src: '/assets/doc-images/time-off/image.png',
        alt: 'Time Off dashboard with yearly calendar leave overview',
        caption: 'Dashboard view showing paid time off balance, mandatory days, public holidays, and the leave calendar.',
      },
      bullets: [
        'Review leave balances directly from the dashboard.',
        'See public holidays and mandatory days alongside scheduled leave.',
        'Switch between different calendar periods for planning.',
      ],
    },
    {
      id: 'employee-self-service',
      title: 'Employee Self-Service',
      body: [
        'The employee self-service area is where staff members request leave and review their current allocations.',
      ],
      subsections: [
        {
          id: 'my-time-off',
          title: 'My Time Off',
          body: [
            'My Time Off is used to apply for leave.',
            'Employees can create a leave request, provide the time off type, select dates, add a description, and track the approval status.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/time-off/image2.png',
              alt: 'Approved My Time Off leave request form',
              caption: 'Leave request form with dates, duration, description, and approval state.',
            },
            {
              src: '/assets/doc-images/time-off/image3.png',
              alt: 'My Time Off list view with approved leave request',
              caption: 'List view of employee leave requests and approval status.',
            },
          ],
          steps: [
            'Open My Time Off and click New.',
            'Select the Time Off Type and dates.',
            'Enter the duration details and a supporting description.',
            'Submit the leave request and follow its approval state.',
          ],
        },
        {
          id: 'my-allocations',
          title: 'My Allocations',
          body: [
            'My Allocations shows how many leaves are allocated to the employee.',
            'This helps employees understand the available leave amount and allocation status before raising a request.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/image4.png',
            alt: 'My Allocations list view with leave amount and status',
            caption: 'My Allocations screen showing the employee leave balance and allocation type.',
          },
        },
      ],
    },
    {
      id: 'overview-and-management',
      title: 'Overview and Management',
      body: [
        'The overview and management area helps HR and managers monitor all leave activity and act on employee requests.',
      ],
      subsections: [
        {
          id: 'overview',
          title: 'Overview',
          body: [
            'The Overview provides a calendar-based view of all employee leaves, allowing users to easily track leave schedules, statuses, and mandatory days.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/image5.png',
            alt: 'All Time Off overview calendar for team leaves',
            caption: 'Overview calendar showing employee leave entries, legend, and mandatory days.',
          },
        },
        {
          id: 'management-time-off',
          title: 'Management Menu: Time Off',
          body: [
            'This menu is used by HR or managers to view and manage all employee leave requests.',
            'Managers can open each request, review the employee and dates, and approve or refuse the leave.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/time-off/image6.png',
              alt: 'Management leave request detail screen',
              caption: 'Management request screen with employee, leave type, dates, duration, and approval action.',
            },
            {
              src: '/assets/doc-images/time-off/image2.png',
              alt: 'Approved time off request detail',
              caption: 'Approved request example from the time off workflow.',
            },
          ],
        },
        {
          id: 'management-allocations',
          title: 'Management Menu: Allocations',
          body: [
            'Allocations are used to allocate leave to employees.',
            'HR and managers can review the employee, time off type, amount, allocation type, and current approval status from the management list.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/image7.png',
            alt: 'Management allocations screen for employees',
            caption: 'Allocation management screen showing approved leave allocations by employee.',
          },
        },
      ],
    },
    {
      id: 'reporting',
      title: 'Reporting Menu',
      body: [
        'The reporting menu is used to analyze leave data from different operational angles.',
      ],
      subsections: [
        {
          id: 'by-employee',
          title: 'By Employee',
          body: [
            'This report is used to view leave data employee-wise.',
            'It shows grouped leave records with number of days, start date, end date, status, and description for each employee.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/image8.png',
            alt: 'Time Off Analysis grouped by employee',
            caption: 'Employee-wise leave analysis with grouped records and day counts.',
          },
        },
        {
          id: 'by-type',
          title: 'By Type',
          body: [
            'This report is used to view leave data based on leave type.',
            'The graph helps teams compare which leave categories are used most frequently.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/image9.png',
            alt: 'Time Off Analysis chart by leave type',
            caption: 'Leave analysis chart comparing counts by time off type.',
          },
        },
      ],
    },
    {
      id: 'configuration',
      title: 'Configuration Menu',
      body: [
        'The configuration menu is used to configure leave settings and policy rules.',
      ],
      subsections: [
        {
          id: 'time-off-types',
          title: 'Time Off Types',
          body: [
            'Time Off Types are used to define different types of leaves.',
            'Examples shown in the system include Paid Time Off, Sick Time Off, Unpaid, and Compensatory Days.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/imagea.png',
            alt: 'Time Off Types list view',
            caption: 'Configured leave types with their approval rules.',
          },
        },
        {
          id: 'accrual-plans',
          title: 'Accrual Plans',
          body: [
            'Accrual Plans are used to automatically allocate leaves over time.',
            'These plans define how leaves are gained, carried over, and applied according to rules.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/imageb.png',
            alt: 'Accrual plan form for monthly leave accrual',
            caption: 'Accrual plan setup with gain timing, carry-over logic, and rule configuration.',
          },
        },
        {
          id: 'public-holidays',
          title: 'Public Holidays',
          body: [
            'Public Holidays are used to define company holidays.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/imagec.png',
            alt: 'Public Holidays list view',
            caption: 'Configured public holiday entry with start and end date.',
          },
        },
        {
          id: 'mandatory-days',
          title: 'Mandatory Days',
          body: [
            'Mandatory Days are used to define days when leave is not allowed.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/imaged.png',
            alt: 'Mandatory Days list view',
            caption: 'Mandatory days setup used to block leave during specific periods.',
          },
        },
        {
          id: 'activity-types',
          title: 'Activity Types',
          body: [
            'Activity Types are used to define activity types for scheduling actions.',
            'They can be used for reminders and approval follow-up activity in the Time Off workflow.',
          ],
          figure: {
            src: '/assets/doc-images/time-off/imagee.png',
            alt: 'Activity Type configuration for approval reminders',
            caption: 'Activity type definition used for approval reminders in the Time Off process.',
          },
        },
      ],
    },
  ],
  related: ['hr', 'payroll', 'calendar'],
};

export default function TimeOffPage() {
  return <DocumentPage document={timeOffDocument} />;
}
